import { paginated, PublicationSchema, WebPubManifestSchema } from '@lectura/ts-contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { apiRequest } from './api';
import { useAuth } from './auth';

const PublicationListSchema = paginated(PublicationSchema);

// A catalog-svc minden GET végpontja `TenantScope`-on át megy, ami kötelezően
// Bearer-tokent kér (`services/_common/lectura_common/auth.py`). Ezért minden
// itteni hook `enabled: isLogged`-del kapcsol — bejelentkezetlen renderben
// semmi nem indul, hogy ne legyen 401-zaj a Network tab-on.

export function useLibrary(params: { search?: string; tag?: string; cursor?: string | null }) {
  const isLogged = useAuth((s) => s.isAuthenticated());
  return useQuery({
    queryKey: ['library', params],
    queryFn: () =>
      apiRequest(PublicationListSchema, '/publications', {
        query: {
          search: params.search,
          tag: params.tag,
          cursor: params.cursor ?? undefined,
          status: 'published',
        },
      }),
    enabled: isLogged,
  });
}

export function usePublication(id: string) {
  const isLogged = useAuth((s) => s.isAuthenticated());
  return useQuery({
    queryKey: ['publication', id],
    queryFn: () => apiRequest(PublicationSchema, `/publications/${id}`),
    enabled: Boolean(id) && isLogged,
  });
}

export function useManifest(publicationId: string) {
  const isLogged = useAuth((s) => s.isAuthenticated());
  return useQuery({
    queryKey: ['manifest', publicationId],
    queryFn: () => apiRequest(WebPubManifestSchema, `/publications/${publicationId}/manifest.json`),
    enabled: Boolean(publicationId) && isLogged,
    staleTime: 60 * 60 * 1000, // 1 óra — a manifest immutable-ish
  });
}

/**
 * Olvasási haladás (progress-svc, port 47088) — eszközök közti szinkron.
 *
 * A backend végpontok:
 *   GET  /me/progress/{publicationId}  → ProgressOut | null
 *   PUT  /me/progress/{publicationId}  → ProgressOut
 *
 * Auth: a hívó felhasználónak be kell jelentkeznie (Bearer JWT). Ha nincs
 * bejelentkezve, a `useProgress` hook `enabled=false`-szal disabled marad
 * (NEM mock — egyszerűen nincs adatforrás). A `useUpdateProgress` mutáció
 * is csak akkor küld hívást, ha van token; nincs lokális fallback.
 */
const ProgressSchema = z.object({
  publicationId: z.string().uuid(),
  cfi: z.string(),
  percent: z.number().int().min(0).max(100),
  deviceId: z.string().nullable().optional(),
  updatedAt: z.string().datetime({ offset: true }),
});
export type Progress = z.infer<typeof ProgressSchema>;

/** Cache-key formátum, hogy a mutáció is invalidálni tudja. */
const progressKey = (publicationId: string) => ['progress', publicationId] as const;
const allProgressKey = ['progress', 'me'] as const;

export function useProgress(publicationId: string) {
  const isLogged = useAuth((s) => s.isAuthenticated());
  return useQuery({
    queryKey: progressKey(publicationId),
    queryFn: () =>
      apiRequest(ProgressSchema.nullable(), `/me/progress/${publicationId}`, {
        baseUrl: '/api/progress',
      }),
    enabled: Boolean(publicationId) && isLogged,
    retry: false,
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Kollekciók — auth-köteles. A backend `TenantScope` dep-et használ
// (`services/catalog-svc/app/routers/collections.py`), így Bearer-token nélkül
// minden hívás 401-et ad. A hookok ezért is `enabled: isLogged`-del kapcsolnak.
// ---------------------------------------------------------------------------

const CollectionSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  coverUrl: z.string().nullable().optional(),
  itemCount: z.number().int().nonnegative(),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});
export type Collection = z.infer<typeof CollectionSchema>;

const CollectionListSchema = z.object({
  data: z.array(CollectionSchema),
});

const CollectionItemSchema = z.object({
  publicationId: z.string().uuid(),
  title: z.string(),
  position: z.number().int().nonnegative(),
});
export type CollectionItem = z.infer<typeof CollectionItemSchema>;

/** A tenant összes kollekciója — kezdőlap, /collections lista, navigáció. */
export function useCollections() {
  const isLogged = useAuth((s) => s.isAuthenticated());
  return useQuery({
    queryKey: ['collections'],
    queryFn: async () => {
      const res = await apiRequest(CollectionListSchema, '/collections');
      return res.data;
    },
    enabled: isLogged,
    staleTime: 5 * 60_000,
  });
}

/** Egy kollekció részletei. */
export function useCollection(collectionId: string) {
  const isLogged = useAuth((s) => s.isAuthenticated());
  return useQuery({
    queryKey: ['collection', collectionId],
    queryFn: () => apiRequest(CollectionSchema, `/collections/${collectionId}`),
    enabled: Boolean(collectionId) && isLogged,
    staleTime: 5 * 60_000,
  });
}

/** Egy kollekció publikáció-tagjai (sort-pozícióval). */
export function useCollectionItems(collectionId: string) {
  const isLogged = useAuth((s) => s.isAuthenticated());
  return useQuery({
    queryKey: ['collection', collectionId, 'items'],
    queryFn: () =>
      apiRequest(z.array(CollectionItemSchema), `/collections/${collectionId}/items`),
    enabled: Boolean(collectionId) && isLogged,
    staleTime: 60_000,
  });
}

/** A felhasználó összes haladás-rekordja — a /shelf „folyamatban" / „befejezett" listáihoz. */
export function useReadingHistory() {
  const isLogged = useAuth((s) => s.isAuthenticated());
  return useQuery({
    queryKey: allProgressKey,
    queryFn: () =>
      apiRequest(z.array(ProgressSchema), '/me/progress', {
        baseUrl: '/api/progress',
      }),
    enabled: isLogged,
    retry: false,
    staleTime: 30_000,
  });
}

/**
 * Haladás-mentés mutáció. A ReaderShell hívja egy throttled callbackből
 * minden szakaszváltáskor, így a backendre nem zúdul minden lapozás.
 *
 * Bejelentkezetlen felhasználónál a mutáció `enabled` flagről nem véd —
 * a hívó kódnak (ReaderShell) kell ellenőriznie és kihagynia a hívást.
 */
export function useUpdateProgress(publicationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { cfi: string; percent: number; deviceId?: string | null }) =>
      apiRequest(ProgressSchema, `/me/progress/${publicationId}`, {
        method: 'PUT',
        baseUrl: '/api/progress',
        body: {
          cfi: body.cfi,
          percent: body.percent,
          device_id: body.deviceId ?? null,
        },
      }),
    onSuccess: (data) => {
      qc.setQueryData(progressKey(publicationId), data);
    },
  });
}
